/**
 * `WindLayer` — a world losing its atmosphere to the solar wind (`aubade-backdrop`), drawn over a
 * `PlanetStage` scene: a ragged glowing limb, gas peeling off the terminator into a long curving
 * tail, and streaks carried past on the wind (`windShaders.ts`).
 *
 * Two parts. The streaks are a `LineSegments` the stage adds to its own scene, placed in the vertex
 * shader from the clock. The gas is a full-screen pass into a small target (sized from CSS pixels,
 * so a high DPR doesn't multiply the march), which `composite` then adds over the frame. The gas
 * depends only on the clock and the camera, never on the planet's spin, so a drag with the clock
 * stopped re-composites the last pass instead of marching again.
 *
 * Its 3D noise is baked on the GPU at construction, one layer per draw, so nothing runs on the main
 * thread per voxel.
 */
import * as THREE from "three";
import { mulberry32 } from "./mulberry32";
import { BAKE_VERTEX } from "./planetShaders";
import type { WindProfile } from "./planetProfiles";
import { COMPOSITE_FRAGMENT, NOISE_BAKE_FRAGMENT, STREAK_FRAGMENT, STREAK_VERTEX, WIND_FRAGMENT } from "./windShaders";

const NOISE_SIZE = 64;

export class WindLayer {
  /** Added to the stage's scene: the planet's depth hides the streaks that pass behind it. */
  readonly streaks: THREE.LineSegments;
  private readonly shared: { uTime: { value: number } };
  private readonly target: THREE.WebGLRenderTarget;
  private readonly noise: THREE.WebGL3DRenderTarget;
  private readonly gasMaterial: THREE.ShaderMaterial;
  private readonly compositeMaterial: THREE.ShaderMaterial;
  private readonly gasScene: THREE.Scene;
  private readonly compositeScene: THREE.Scene;
  private readonly flat = new THREE.Camera();
  private readonly disposables: Array<{ dispose(): void }> = [];
  private readonly resolution: number;
  /** The clock reading the target was last drawn at; undefined when the camera or size changed. */
  private drawnAt: number | undefined;

  constructor(renderer: THREE.WebGLRenderer, opts: { wind: WindProfile; light: THREE.Vector3 }) {
    const { wind, light } = opts;
    this.resolution = wind.resolution;
    const dir = light.clone().negate();
    // U: screen-up made perpendicular to the wind; the tail bends toward it.
    const up = new THREE.Vector3(0, 1, 0).addScaledVector(dir, -dir.y).normalize();
    const side = new THREE.Vector3().crossVectors(dir, up).normalize();
    const shared = {
      uWind: { value: dir },
      uWindU: { value: up },
      uWindV: { value: side },
      uLight: { value: light },
      uTime: { value: 0 },
      uBend: { value: wind.bend },
    };
    this.shared = shared;

    const streakMaterial = this.own(
      new THREE.ShaderMaterial({
        vertexShader: STREAK_VERTEX,
        fragmentShader: STREAK_FRAGMENT,
        uniforms: { ...shared, uColor: { value: new THREE.Color(...wind.streaks.color) } },
        blending: THREE.AdditiveBlending,
        transparent: true,
        depthWrite: false,
      }),
    );
    this.streaks = new THREE.LineSegments(this.own(streakGeometry(wind.streaks.count)), streakMaterial);
    // Positions come from the shader; the geometry's own are all zero.
    this.streaks.frustumCulled = false;

    this.noise = this.bakeNoise(renderer);
    this.target = this.own(new THREE.WebGLRenderTarget(1, 1, { type: THREE.HalfFloatType, depthBuffer: false }));
    this.gasMaterial = this.own(
      new THREE.ShaderMaterial({
        vertexShader: BAKE_VERTEX,
        fragmentShader: WIND_FRAGMENT,
        uniforms: {
          ...shared,
          uInvProj: { value: new THREE.Matrix4() },
          uCamWorld: { value: new THREE.Matrix4() },
          uCamPos: { value: new THREE.Vector3() },
          uLength: { value: wind.length },
          uFlowSpeed: { value: wind.flowSpeed },
          uLimb: { value: wind.limb },
          uTail: { value: wind.tail },
          uLimbColor: { value: new THREE.Color(...wind.limbColor) },
          uCoreColor: { value: new THREE.Color(...wind.coreColor) },
          uTailColor: { value: new THREE.Color(...wind.tailColor) },
          uNoise: { value: this.noise.texture },
        },
        depthTest: false,
        depthWrite: false,
      }),
    );
    this.compositeMaterial = this.own(
      new THREE.ShaderMaterial({
        vertexShader: BAKE_VERTEX,
        fragmentShader: COMPOSITE_FRAGMENT,
        uniforms: { uWindTex: { value: this.target.texture }, uTexel: { value: new THREE.Vector2() } },
        blending: THREE.AdditiveBlending,
        transparent: true,
        depthTest: false,
        depthWrite: false,
      }),
    );
    this.gasScene = this.fullscreen(this.gasMaterial);
    this.compositeScene = this.fullscreen(this.compositeMaterial);
  }

  /** Size the gas target from the viewport's CSS pixels; the camera has just changed too. */
  setSize(width: number, height: number): void {
    const w = Math.max(1, Math.ceil(width * this.resolution));
    const h = Math.max(1, Math.ceil(height * this.resolution));
    this.target.setSize(w, h);
    this.compositeMaterial.uniforms.uTexel!.value.set(1 / w, 1 / h);
    this.drawnAt = undefined;
  }

  /** Advance the streaks to the clock. Call before the stage renders its scene. */
  setTime(time: number): void {
    this.shared.uTime.value = time;
  }

  /** Draw the gas (only if the clock or the camera moved since last time) and add it over the frame. */
  composite(renderer: THREE.WebGLRenderer, camera: THREE.PerspectiveCamera): void {
    const time = this.shared.uTime.value;
    if (this.drawnAt !== time) {
      const u = this.gasMaterial.uniforms;
      camera.updateMatrixWorld();
      u.uInvProj!.value.copy(camera.projectionMatrixInverse);
      u.uCamWorld!.value.copy(camera.matrixWorld);
      u.uCamPos!.value.copy(camera.position);
      renderer.setRenderTarget(this.target);
      renderer.render(this.gasScene, this.flat);
      renderer.setRenderTarget(null);
      this.drawnAt = time;
    }
    const autoClear = renderer.autoClear;
    renderer.autoClear = false;
    renderer.render(this.compositeScene, this.flat);
    renderer.autoClear = autoClear;
  }

  dispose(): void {
    for (const d of this.disposables) d.dispose();
  }

  // ── internals ────────────────────────────────────────────────────────────────

  private own<T extends { dispose(): void }>(thing: T): T {
    this.disposables.push(thing);
    return thing;
  }

  private fullscreen(material: THREE.ShaderMaterial): THREE.Scene {
    const g = this.own(new THREE.BufferGeometry());
    g.setAttribute("position", new THREE.Float32BufferAttribute([-1, -1, 0, 3, -1, 0, -1, 3, 0], 3));
    g.setAttribute("uv", new THREE.Float32BufferAttribute([0, 0, 2, 0, 0, 2], 2));
    const mesh = new THREE.Mesh(g, material);
    mesh.frustumCulled = false;
    const scene = new THREE.Scene();
    scene.add(mesh);
    return scene;
  }

  /** Fill a tileable size³ noise texture on the GPU, one z-layer per draw. */
  private bakeNoise(renderer: THREE.WebGLRenderer): THREE.WebGL3DRenderTarget {
    const target = this.own(new THREE.WebGL3DRenderTarget(NOISE_SIZE, NOISE_SIZE, NOISE_SIZE, { depthBuffer: false }));
    const tex = target.texture;
    tex.minFilter = THREE.LinearFilter;
    tex.magFilter = THREE.LinearFilter;
    tex.wrapS = THREE.RepeatWrapping;
    tex.wrapT = THREE.RepeatWrapping;
    tex.wrapR = THREE.RepeatWrapping;
    const material = new THREE.ShaderMaterial({
      vertexShader: BAKE_VERTEX,
      fragmentShader: NOISE_BAKE_FRAGMENT,
      uniforms: { uLayer: { value: 0 }, uSize: { value: NOISE_SIZE } },
      depthTest: false,
    });
    const scene = this.fullscreen(material);
    for (let z = 0; z < NOISE_SIZE; z++) {
      material.uniforms.uLayer!.value = z;
      renderer.setRenderTarget(target, z);
      renderer.render(scene, this.flat);
    }
    renderer.setRenderTarget(null);
    material.dispose();
    return target;
  }
}

/**
 * Streaks: most are gas peeling off the limb ring (offset just past one radius), the rest the bare
 * wind passing wide of the planet. Each loops downwind over its span on the shared clock: long,
 * faint and unhurried, so they read as wind rather than rain.
 */
function streakGeometry(count: number): THREE.BufferGeometry {
  const rand = mulberry32(77);
  const n = count * 2;
  const offset = new Float32Array(n * 2);
  const start = new Float32Array(n);
  const span = new Float32Array(n);
  const speed = new Float32Array(n);
  const phase = new Float32Array(n);
  const len = new Float32Array(n);
  const end = new Float32Array(n);
  const glow = new Float32Array(n);
  for (let i = 0; i < count; i++) {
    const limb = rand() < 0.8;
    const ang = rand() * Math.PI * 2;
    const r = limb ? 1.0 + Math.pow(rand(), 2) * 0.35 : 1.4 + rand() * 3.2;
    const s = {
      start: limb ? -0.35 + rand() * 0.3 : -6 + rand() * 2,
      span: limb ? 5 + rand() * 4 : 12 + rand() * 4,
      speed: (limb ? 0.55 : 0.95) * (0.7 + rand() * 0.6),
      len: limb ? 0.6 + rand() * 0.9 : 0.9 + rand() * 1.4,
      phase: rand(),
      glow: (limb ? 0.38 : 0.05) * (0.5 + rand()),
    };
    for (let e = 0; e < 2; e++) {
      const j = i * 2 + e;
      offset.set([Math.cos(ang) * r, Math.sin(ang) * r], j * 2);
      start[j] = s.start;
      span[j] = s.span;
      speed[j] = s.speed;
      phase[j] = s.phase;
      len[j] = s.len;
      end[j] = e;
      glow[j] = s.glow;
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute("position", new THREE.BufferAttribute(new Float32Array(n * 3), 3));
  g.setAttribute("aOffset", new THREE.BufferAttribute(offset, 2));
  g.setAttribute("aStart", new THREE.BufferAttribute(start, 1));
  g.setAttribute("aSpan", new THREE.BufferAttribute(span, 1));
  g.setAttribute("aSpeed", new THREE.BufferAttribute(speed, 1));
  g.setAttribute("aPhase", new THREE.BufferAttribute(phase, 1));
  g.setAttribute("aLen", new THREE.BufferAttribute(len, 1));
  g.setAttribute("aEnd", new THREE.BufferAttribute(end, 1));
  g.setAttribute("aGlow", new THREE.BufferAttribute(glow, 1));
  return g;
}
